import { useState } from 'react';
import { useMutation, gql } from '@/apollo';
import type { Insurance, InsuranceFormState } from '../types/moderator.types';
import { initialInsuranceState } from '../types/moderator.types';
// import { getErrorMessage, logError } from '../../../utils/errorMessages'; // Reserved for future use

// GraphQL Mutations
const CREATE_INSURANCE = gql`
  mutation CreateInsuranceModeratorDashboard($input: CreateInsuranceInput!) {
    createInsuranceModeratorDashboard(input: $input) {
      id
      name
      description
      isActive
      customerId
      customer {
        id
        name
      }
    }
  }
`;

const UPDATE_INSURANCE = gql`
  mutation UpdateInsuranceModeratorDashboard($id: ID!, $input: UpdateInsuranceInput!) {
    updateInsuranceModeratorDashboard(id: $id, input: $input) {
      id
      name
      description
      isActive
      customerId
      customer {
        id
        name
      }
    }
  }
`;

const DELETE_INSURANCE = gql`
  mutation DeleteInsuranceModeratorDashboard($id: ID!) {
    deleteInsuranceModeratorDashboard(id: $id)
  }
`;

export interface UseInsuranceManagementReturn {
  // State
  showForm: boolean;
  editing: Insurance | null;
  form: InsuranceFormState;
  searchTerm: string;
  showDeleteModal: boolean;
  deleteId: string | null;
  
  // State setters
  setShowForm: (show: boolean) => void;
  setEditing: (insurance: Insurance | null) => void;
  setForm: (form: InsuranceFormState) => void;
  setSearchTerm: (term: string) => void;
  
  // Handlers
  handleCreate: (customerId?: string) => void;
  handleEdit: (insurance: Insurance) => void;
  handleDelete: (id: string) => void;
  handleConfirmDelete: () => void;
  handleCancelDelete: () => void;
  handleSubmit: (e: React.FormEvent) => void;
  handleCancel: () => void;
  
  // Loading states
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
}

export const useInsuranceManagement = (
  refetchInsurances: () => void,
  refetchModeratorData: () => void
): UseInsuranceManagementReturn => {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Insurance | null>(null);
  const [form, setForm] = useState<InsuranceFormState>(initialInsuranceState);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const [createInsurance, { loading: isCreating }] = useMutation(CREATE_INSURANCE, {
    onCompleted: async () => {
      setShowForm(false);
      setEditing(null);
      setForm(initialInsuranceState);
      await refetchModeratorData();
      await refetchInsurances();
    },
    onError: (error) => {
      console.error('Create Insurance error:', error);
    },
    refetchQueries: ['GetModeratorData', 'GetInsurances']
  });

  const [updateInsurance, { loading: isUpdating }] = useMutation(UPDATE_INSURANCE, {
    onCompleted: async () => {
      setShowForm(false);
      setEditing(null);
      setForm(initialInsuranceState);
      await refetchModeratorData();
      await refetchInsurances();
    },
    onError: (error) => {
      console.error('[Update Insurance]', error);
    },
    refetchQueries: ['GetModeratorData', 'GetInsurances']
  });

  const [deleteInsurance, { loading: isDeleting }] = useMutation(DELETE_INSURANCE, {
    onCompleted: async () => {
      setShowDeleteModal(false);
      setDeleteId(null);
      await refetchModeratorData();
      await refetchInsurances();
    },
    onError: (error) => {
      console.error('[Delete Insurance]', error);
    },
    refetchQueries: ['GetModeratorData', 'GetInsurances']
  });

  const handleCreate = (customerId?: string) => {
    setShowForm(true);
    setEditing(null);
    setForm({ ...initialInsuranceState, customerId: customerId || '' });
  };

  const handleEdit = (insurance: Insurance) => {
    setShowForm(true);
    setEditing(insurance);
    setForm({
      name: insurance.name,
      description: insurance.description || '',
      isActive: insurance.isActive,
      customerId: insurance.customerId || ''
    });
  };

  const handleDelete = (id: string) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (deleteId) {
      try {
        await deleteInsurance({ variables: { id: deleteId } });
        // Modal will be closed in onCompleted callback
      } catch (error) {
        // Error is already logged in onError callback
        // Don't close modal on error - let user retry or cancel
      }
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const input = {
      name: form.name,
      description: form.description || null,
      isActive: form.isActive,
      customerId: form.customerId || null
    };
    if (editing) {
      await updateInsurance({
        variables: {
          id: editing.id,
          input
        }
      });
    } else {
      await createInsurance({
        variables: {
          input
        }
      });
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditing(null);
    setForm(initialInsuranceState);
  };

  return {
    showForm,
    editing,
    form,
    searchTerm,
    showDeleteModal,
    deleteId,
    setShowForm,
    setEditing,
    setForm,
    setSearchTerm,
    handleCreate,
    handleEdit,
    handleDelete,
    handleConfirmDelete,
    handleCancelDelete,
    handleSubmit,
    handleCancel,
    isCreating,
    isUpdating,
    isDeleting,
  };
};

